import {
  CDefaultLocale,
  ECurrency,
  type ELocales,
  type TDiscount,
} from '@common/types';

/**
 * Applies a discount to a price based on the discount type and value.
 * @param {number} price - The original price.
 * @param {TDiscount} discount - The discount object containing the discount type and value.
 * @returns {number} The discounted price.
 */
export function applyDiscount(
  price: number,
  discount: TDiscount,
): number | string {
  switch (discount.type) {
    case 'PERCENT':
      return (price - (price * discount.value) / 100).toFixed(2) as string;
    case 'FIXED':
      return price - discount.value;
    default:
      return price;
  }
}

/**
 * Converts a number to a currency string.
 * @param {number} number - The number to convert.
 * @param {ELocales} locale - The locale to use for formatting.
 * @param {ECurrency} currency - The currency to use for formatting.
 * @returns {string} The formatted currency string.
 */
export const numberToCurrency = (
  number: number,
  locale: ELocales = CDefaultLocale,
  currency: ECurrency = ECurrency.USD,
): string => {
  const formatterCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });

  return formatterCurrency.format(number);
};
